import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  type Firestore,
  type DocumentData,
} from 'firebase/firestore'
import { httpsCallable, type Functions } from 'firebase/functions'
import type { Tag, OwnerTheme } from '@/types'
import { toDate } from '@/utils/timestamp'

/**
 * tagService — reads and owner-permitted writes for tags.
 *
 * Server-authoritative fields (`provisioned`, `ownerClaimed`, `ownerId`,
 * `totalTaps`) are never written here directly; claiming a tag goes through the
 * `onTagClaimed` callable so the write happens via the Admin SDK atomically.
 */

/** Map a raw `tags/{id}` document to a typed Tag (Timestamp -> Date). */
const mapTag = (tagId: string, data: DocumentData): Tag => ({
  tagId,
  ownerId: data.ownerId ?? null,
  ownerClaimed: data.ownerClaimed ?? false,
  provisioned: data.provisioned ?? false,
  name: data.name ?? null,
  ownerTheme: data.ownerTheme
    ? {
        accentColor: data.ownerTheme.accentColor,
        backgroundStyle: data.ownerTheme.backgroundStyle,
        motif: data.ownerTheme.motif ?? null,
        pinnedMessage: data.ownerTheme.pinnedMessage ?? '',
        sourceThemeId: data.ownerTheme.sourceThemeId ?? null,
      }
    : null,
  themeId: data.themeId ?? null,
  variant: data.variant ?? 'standard',
  totalTaps: data.totalTaps ?? 0,
  createdAt: toDate(data.createdAt),
})

/**
 * Fetch a tag by its id — the entry read for `/t/{tagId}` (the id encoded on
 * the chip). Returns null if it does not exist.
 */
export const getTagById = async (
  db: Firestore,
  tagId: string,
): Promise<Tag | null> => {
  const snap = await getDoc(doc(db, 'tags', tagId))
  if (!snap.exists()) return null
  return mapTag(snap.id, snap.data())
}

/**
 * Fetch every tag owned by a uid, newest first. Powers the account "My Tags"
 * list. Requires a composite index on (ownerId asc, createdAt desc).
 */
export const getTagsByOwner = async (
  db: Firestore,
  ownerId: string,
): Promise<Tag[]> => {
  const snap = await getDocs(
    query(
      collection(db, 'tags'),
      where('ownerId', '==', ownerId),
      orderBy('createdAt', 'desc'),
    ),
  )
  return snap.docs.map((d) => mapTag(d.id, d.data()))
}

/**
 * Fetch every tag a user is a *member* of — i.e. has posted to (tracked by the
 * `tagMembers` registry). Used alongside owned tags on the account screen.
 * Tags the user also owns are included; the caller dedupes/badges.
 */
export const getMemberTags = async (
  db: Firestore,
  userId: string,
): Promise<Tag[]> => {
  const snap = await getDocs(
    query(collection(db, 'tagMembers'), where('userId', '==', userId)),
  )
  const tagIds = [...new Set(snap.docs.map((d) => d.data().tagId as string))]
  const tags = await Promise.all(tagIds.map((id) => getTagById(db, id)))
  return tags.filter((b): b is Tag => b !== null)
}

/**
 * Update the owner-controlled theme on a tag. Owners may freely change this;
 * passing an `ownerTheme` whose `sourceThemeId` is preserved enables later
 * "reset to original". Does not touch any server-authoritative field.
 */
export const updateOwnerTheme = async (
  db: Firestore,
  tagId: string,
  ownerTheme: OwnerTheme,
): Promise<void> => {
  await updateDoc(doc(db, 'tags', tagId), { ownerTheme })
}

/**
 * Update the owner-editable cosmetic identity of an already-claimed tag: its
 * name and theme, in one atomic write. Powers the post-claim "Edit tag" flow.
 * Mirrors `firestore.rules`, which allows the owner to touch exactly these two
 * fields (`hasOnly(['ownerTheme', 'name'])`) — everything else (ownership,
 * tap counts, the trail) stays server-only and immutable.
 */
export const updateTagCustomization = async (
  db: Firestore,
  tagId: string,
  name: string,
  ownerTheme: OwnerTheme,
): Promise<void> => {
  await updateDoc(doc(db, 'tags', tagId), { name, ownerTheme })
}

/** Payload for claiming a tag during onboarding. */
export interface ClaimOwnershipParams {
  tagId: string
  name: string
  ownerTheme: OwnerTheme
}

/**
 * Claim a tag for the current authenticated user. Delegates to the
 * `onTagClaimed` Cloud Function, which atomically sets `provisioned`,
 * `ownerClaimed`, and `ownerId` via the Admin SDK — the client cannot write
 * those fields directly.
 */
export const claimOwnership = async (
  functions: Functions,
  params: ClaimOwnershipParams,
): Promise<void> => {
  const callable = httpsCallable<ClaimOwnershipParams, void>(
    functions,
    'onTagClaimed',
  )
  await callable(params)
}
